import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { usePage } from "@/contexts/PageContext";
import { initialCheckoutStepSlugFromSession } from "@/lib/checkoutInitialStepSlug";
import { normalizeCheckoutPhone } from "@/lib/normalizeCheckoutPhone";
import { CheckoutFlow } from "./CheckoutFlow";

function contactPrefillFromSearchParams(searchParams: URLSearchParams) {
  const email = searchParams.get("email")?.trim() ?? "";
  const rawPhone = searchParams.get("phone")?.trim() ?? "";
  const parsed = normalizeCheckoutPhone(rawPhone);
  const phone = parsed.ok ? (parsed.e164 ?? "") : rawPhone;
  return { email, phone };
}

function titleForCheckoutStep(stepParam: string | undefined): string {
  if (stepParam === "shipping") return "Checkout · Shipping";
  if (stepParam === "payment") return "Checkout · Payment";
  return "Checkout · Contact";
}

export function CheckoutRoute() {
  const { checkoutId, step } = useParams<{ checkoutId: string; step: string }>();
  const [searchParams] = useSearchParams();
  const { setStripe, setCheckoutSettings } = usePage();
  const [data, setData] = useState<{
    products: any[];
    lineItems: Array<{ productId: string; variantId?: string }>;
    checkoutSession?: {
      savePaymentMethod?: boolean;
      submitBtnText?: string;
      customerEmail?: string;
      customerPhone?: string;
      shipping?: {
        name: string;
        address: {
          line1: string;
          line2?: string;
          city: string;
          state: string;
          country: string;
          postal_code: string;
        };
      };
      contactCustomFields?: Array<{
        key: string;
        label: string;
        required?: boolean;
        options: string[];
      }>;
      contactCustomFieldValues?: Record<string, string>;
    };
    stripe: { publishableKey: string; accountId?: string } | null;
    checkoutSettings: any;
    paymentIntentLastErrorMessage?: string;
    setupIntentLastErrorMessage?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!checkoutId) return;

    const fetchCheckout = async () => {
      let holdLoadingUntilNavigate = false;
      try {
        const response = await fetch(`/api/checkout/${checkoutId}`);
        if (response.ok) {
          const result = await response.json();
          const paidRedirect =
            typeof result.checkoutAlreadyPaidRedirect === "string"
              ? result.checkoutAlreadyPaidRedirect.trim()
              : "";
          if (paidRedirect) {
            holdLoadingUntilNavigate = true;
            window.location.replace(paidRedirect);
            return;
          }
          const c = result.checkout;
          const piErr =
            typeof result.paymentIntentLastErrorMessage === "string"
              ? result.paymentIntentLastErrorMessage.trim()
              : "";
          const siErr =
            typeof result.setupIntentLastErrorMessage === "string"
              ? result.setupIntentLastErrorMessage.trim()
              : "";
          setData({
            products: result.products || [],
            lineItems: c?.lineItems || [],
            checkoutSession: {
              ...(c?.savePaymentMethod === true ? { savePaymentMethod: true } : {}),
              ...(typeof c?.submitBtnText === "string" && c.submitBtnText.trim() !== ""
                ? { submitBtnText: c.submitBtnText.trim() }
                : {}),
              customerEmail: c?.customerEmail,
              customerPhone: c?.customerPhone,
              shipping: c?.shipping,
              contactCustomFields: c?.contactCustomFields,
              contactCustomFieldValues: c?.contactCustomFieldValues,
            },
            stripe: result.stripe || null,
            checkoutSettings: result.checkoutSettings || null,
            ...(piErr ? { paymentIntentLastErrorMessage: piErr } : {}),
            ...(siErr ? { setupIntentLastErrorMessage: siErr } : {}),
          });
          if (result.stripe) {
            setStripe(result.stripe);
          }
          if (result.checkoutSettings) {
            setCheckoutSettings(result.checkoutSettings);
          }
        }
      } catch (error) {
        console.error("Error fetching checkout:", error);
      } finally {
        if (!holdLoadingUntilNavigate) setLoading(false);
      }
    };

    fetchCheckout();
  }, [checkoutId, setStripe, setCheckoutSettings]);

  if (checkoutId && step === "email") {
    return (
      <>
        <Helmet>
          <title>Checkout · Contact</title>
        </Helmet>
        <Navigate to={`/checkout/${checkoutId}/contact`} replace />
      </>
    );
  }

  if (checkoutId && !step) {
    if (loading) {
      return (
        <>
          <Helmet>
            <title>Checkout</title>
          </Helmet>
          <div className="min-h-screen flex items-center justify-center">Loading...</div>
        </>
      );
    }
    if (!data) {
      return (
        <>
          <Helmet>
            <title>Checkout · Contact</title>
          </Helmet>
          <Navigate to={`/checkout/${checkoutId}/contact`} replace />
        </>
      );
    }
    const { email: urlEmail, phone: urlPhone } = contactPrefillFromSearchParams(searchParams);
    const slug = initialCheckoutStepSlugFromSession({
      session: {
        ...data.checkoutSession,
        customerEmail: urlEmail || data.checkoutSession?.customerEmail || "",
        customerPhone: urlPhone || data.checkoutSession?.customerPhone || "",
      },
      products: data.products,
      lineItems: data.lineItems,
    });
    return (
      <>
        <Helmet>
          <title>{titleForCheckoutStep(slug)}</title>
        </Helmet>
        <Navigate to={`/checkout/${checkoutId}/${slug}`} replace />
      </>
    );
  }

  if (loading || !data || !checkoutId) {
    return (
      <>
        <Helmet>
          <title>{titleForCheckoutStep(step)}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">Loading...</div>
      </>
    );
  }

  const validSteps = ["contact", "shipping", "payment"];
  if (step && !validSteps.includes(step)) {
    return (
      <>
        <Helmet>
          <title>Checkout · Contact</title>
        </Helmet>
        <Navigate to={`/checkout/${checkoutId}/contact`} replace />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{titleForCheckoutStep(step)}</title>
      </Helmet>
      <CheckoutFlow
        checkoutId={checkoutId}
        products={data.products}
        lineItems={data.lineItems}
        checkoutSession={data.checkoutSession}
        stripe={data.stripe}
        checkoutSettings={data.checkoutSettings}
        paymentIntentLastErrorMessage={data.paymentIntentLastErrorMessage}
        setupIntentLastErrorMessage={data.setupIntentLastErrorMessage}
      />
    </>
  );
}
